// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

// Automatic price sync. Collects the securities the user actually HOLDS
// (nonzero shares) plus the foreign currencies their accounts use, and fetches
// current values through the sync-server's price proxy (/prices/*) — never
// directly from third-party providers, so the user's IP and asset list stay
// between the client and their own server. See sync-server/src/price-proxy.ts
// for the proxy's privacy guarantees.
//
// Fetched values become ordinary PriceAdded events in the encrypted ledger:
//  - securities:      value = price × 100 (integer cents, app-wide convention)
//  - exchange rates:  value = rate × RATE_PRECISION, commodityId = currency
// One price per commodity per day; anything that already has a price dated
// today is skipped BEFORE any network request, which makes the on-unlock
// auto-run idempotent and quiet.

import type { LedgerState, Price, PriceAddedPayload } from '../types.js';
import { isInvestmentAccount, calculateSharesHeld, getCommodityId } from './investment.js';
import { RATE_PRECISION } from './currency-math.js';
import { v4 as uuidv4 } from 'uuid';

// ── Symbol resolution ────────────────────────────────────────────────────────

/** Common crypto tickers → CoinGecko ids. Unlisted tickers fall through to the
 *  equity provider (and are reported as unresolved if that also fails). */
export const KNOWN_CRYPTO: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  USDT: 'tether',
  BNB: 'binancecoin',
  SOL: 'solana',
  USDC: 'usd-coin',
  XRP: 'ripple',
  DOGE: 'dogecoin',
  ADA: 'cardano',
  TRX: 'tron',
  AVAX: 'avalanche-2',
  DOT: 'polkadot',
  LINK: 'chainlink',
  MATIC: 'matic-network',
  POL: 'polygon-ecosystem-token',
  LTC: 'litecoin',
  BCH: 'bitcoin-cash',
  UNI: 'uniswap',
  XLM: 'stellar',
  ETC: 'ethereum-classic',
  ATOM: 'cosmos',
  XMR: 'monero',
  FIL: 'filecoin',
  APT: 'aptos',
  ARB: 'arbitrum',
  OP: 'optimism',
  NEAR: 'near',
  ALGO: 'algorand',
  AAVE: 'aave',
  MKR: 'maker',
  COMP: 'compound-governance-token',
  CRV: 'curve-dao-token',
  SNX: 'havven',
  SHIB: 'shiba-inu',
  PEPE: 'pepe',
  SUI: 'sui',
  TON: 'the-open-network',
  ICP: 'internet-computer',
  HBAR: 'hedera-hashgraph',
  VET: 'vechain',
  EOS: 'eos',
  XTZ: 'tezos',
  ZEC: 'zcash',
  DASH: 'dash',
  KSM: 'kusama',
  GRT: 'the-graph',
  SAND: 'the-sandbox',
  MANA: 'decentraland',
  ENJ: 'enjincoin',
  CHZ: 'chiliz',
};

/** US stock-market exchange labels — a ticker on one of these is never crypto,
 *  even if it collides with a coin ticker (e.g. COIN on NASDAQ). */
const STOCK_EXCHANGES = /nasdaq|nyse|amex|arca|bats|lse|xetra|tsx|euronext|asx|bourse|börse/i;
const CRYPTO_EXCHANGES = /crypto|coingecko|coinbase|binance|kraken|gemini|bitstamp/i;

/** Fiat currencies frankfurter.app (ECB reference rates) can quote. */
export const FRANKFURTER_CURRENCIES = new Set([
  'AUD', 'BGN', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD',
  'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD',
  'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB', 'TRY', 'USD', 'ZAR',
]);

export type HoldingKind = 'crypto' | 'equity';

export function classifyHolding(ticker: string, exchange: string): HoldingKind {
  const t = ticker.toUpperCase();
  if (CRYPTO_EXCHANGES.test(exchange)) return 'crypto';
  if (STOCK_EXCHANGES.test(exchange)) return 'equity';
  return t in KNOWN_CRYPTO ? 'crypto' : 'equity';
}

/** Equity symbols go to Alpha Vantage verbatim (upper-cased): AAPL, VTSAX,
 *  BRK-B, BMW.DEX. Users with non-US listings write the AV-qualified symbol. */
export function equitySymbol(ticker: string): string {
  return ticker.toUpperCase();
}

// ── Plan collection ──────────────────────────────────────────────────────────

export interface PriceSyncPlan {
  /** commodityId → CoinGecko id */
  crypto: Map<string, string>;
  /** commodityId → Alpha Vantage symbol */
  equity: Map<string, string>;
  /** ISO currency codes needing a rate vs homeCurrency */
  fx: string[];
  /** commodityIds skipped because a price already exists today */
  upToDate: string[];
}

function hasPriceToday(prices: Map<string, Price>, commodityId: string, today: string): boolean {
  for (const p of prices.values()) {
    if (p.commodityId === commodityId && p.date === today) return true;
  }
  return false;
}

/** Securities with nonzero shares, plus foreign fiat currencies in use —
 *  minus anything that already has a price dated `today`. */
export function collectPriceSyncPlan(
  ledger: LedgerState,
  homeCurrency: string,
  today: string,
): PriceSyncPlan {
  const plan: PriceSyncPlan = { crypto: new Map(), equity: new Map(), fx: [], upToDate: [] };
  const transactions = [...ledger.transactions.values()];
  const fxWanted = new Set<string>();

  for (const account of ledger.accounts.values()) {
    if (isInvestmentAccount(account)) {
      const shares = calculateSharesHeld(account.id, transactions);
      if (shares <= 0) continue;
      const commodityId = getCommodityId(account);
      const ticker = account.commodity?.ticker ?? '';
      if (!ticker) continue; // no symbol to look up
      if (hasPriceToday(ledger.prices, commodityId, today)) {
        plan.upToDate.push(commodityId);
        continue;
      }
      const exchange = account.commodity?.exchange ?? '';
      if (classifyHolding(ticker, exchange) === 'crypto') {
        const geckoId = KNOWN_CRYPTO[ticker.toUpperCase()];
        if (geckoId) plan.crypto.set(commodityId, geckoId);
        else plan.equity.set(commodityId, equitySymbol(ticker));
      } else {
        plan.equity.set(commodityId, equitySymbol(ticker));
      }
    } else if (account.currency && account.currency !== homeCurrency) {
      fxWanted.add(account.currency.toUpperCase());
    }
  }

  for (const code of fxWanted) {
    if (!FRANKFURTER_CURRENCIES.has(code)) continue; // crypto/exotic: no ECB rate
    if (hasPriceToday(ledger.prices, code, today)) plan.upToDate.push(code);
    else plan.fx.push(code);
  }
  plan.fx.sort();
  return plan;
}

// ── Fetching & parsing ───────────────────────────────────────────────────────

export interface PriceSyncResult {
  events: PriceAddedPayload[];
  /** commodityIds we tried but could not price (unknown symbol, no data). */
  unresolved: string[];
  /** commodityIds skipped because today's price already existed. */
  upToDate: string[];
  /** Equity commodityIds skipped because no Alpha Vantage key is configured. */
  needsApiKey: string[];
}

type FetchFn = typeof fetch;

async function proxyGet(
  baseUrl: string,
  token: string,
  path: string,
  fetchFn: FetchFn,
  extraHeaders: Record<string, string> = {},
): Promise<string> {
  const res = await fetchFn(`${baseUrl}${path}`, {
    headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
  });
  if (!res.ok) throw new Error(`price proxy ${path.split('?')[0]} failed (${res.status})`);
  return res.text();
}

function priceEvent(
  commodityId: string,
  currency: string,
  date: string,
  value: number,
  source: string,
  type: PriceAddedPayload['type'],
): PriceAddedPayload {
  return { priceId: uuidv4(), commodityId, currency, date, source, type, value };
}

/**
 * Execute a sync plan through the price proxy. Pure I/O orchestration — the
 * caller wraps the returned payloads in events and pushes them.
 */
export async function runPriceSync(
  plan: PriceSyncPlan,
  opts: {
    baseUrl: string;
    token: string;
    homeCurrency: string;
    today: string;
    /** Alpha Vantage key for equity quotes; null skips equities. */
    apiKey?: string | null;
    fetchFn?: FetchFn;
  },
): Promise<PriceSyncResult> {
  const { baseUrl, token, homeCurrency, today } = opts;
  const fetchFn = opts.fetchFn ?? fetch;
  const events: PriceAddedPayload[] = [];
  const unresolved: string[] = [];
  const needsApiKey: string[] = [];

  if (plan.crypto.size > 0) {
    const ids = [...new Set(plan.crypto.values())];
    const body = await proxyGet(
      baseUrl, token,
      `/prices/crypto?ids=${ids.join(',')}&vs=${homeCurrency.toLowerCase()}`,
      fetchFn,
    );
    const parsed = JSON.parse(body) as Record<string, Record<string, number>>;
    for (const [commodityId, geckoId] of plan.crypto) {
      const price = parsed[geckoId]?.[homeCurrency.toLowerCase()];
      if (typeof price === 'number' && price > 0) {
        const cents = Math.round(price * 100);
        if (cents > 0) {
          events.push(priceEvent(commodityId, homeCurrency, today, cents, 'auto:coingecko', 'last'));
          continue;
        }
      }
      unresolved.push(commodityId);
    }
  }

  if (plan.equity.size > 0) {
    if (!opts.apiKey) {
      // Equities need the user's (free) Alpha Vantage key — crypto and FX
      // stay keyless. Report separately so the UI can point at Settings.
      needsApiKey.push(...plan.equity.keys());
    } else {
      const symbols = [...new Set(plan.equity.values())];
      const body = await proxyGet(
        baseUrl, token,
        `/prices/equity?symbols=${symbols.join(',')}`,
        fetchFn,
        { 'X-Price-Api-Key': opts.apiKey },
      );
      const quotes = JSON.parse(body) as Record<string, number>;
      for (const [commodityId, symbol] of plan.equity) {
        const price = quotes[symbol];
        if (typeof price === 'number' && price > 0) {
          const cents = Math.round(price * 100);
          events.push(priceEvent(commodityId, homeCurrency, today, cents, 'auto:alphavantage', 'last'));
        } else {
          unresolved.push(commodityId);
        }
      }
    }
  }

  if (plan.fx.length > 0) {
    const body = await proxyGet(
      baseUrl, token,
      `/prices/fx?base=${homeCurrency}&symbols=${plan.fx.join(',')}`,
      fetchFn,
    );
    const parsed = JSON.parse(body) as { rates?: Record<string, number> };
    for (const code of plan.fx) {
      const perHome = parsed.rates?.[code];
      // frankfurter returns home→foreign; the ledger stores foreign→home
      // (1 unit of `commodityId` = value/RATE_PRECISION of homeCurrency).
      if (typeof perHome === 'number' && perHome > 0) {
        const rate = Math.round((1 / perHome) * RATE_PRECISION);
        events.push(priceEvent(code, homeCurrency, today, rate, 'auto:ecb', 'last'));
      } else {
        unresolved.push(code);
      }
    }
  }

  return { events, unresolved, upToDate: plan.upToDate, needsApiKey };
}

/** True when the account set contains anything the sync could price. */
export function planIsEmpty(plan: PriceSyncPlan): boolean {
  return plan.crypto.size === 0 && plan.equity.size === 0 && plan.fx.length === 0;
}
